import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const LocationContext = createContext();

const DEFAULT_LOCATION = {
    city: "All Locations",
    area: "Marketplace",
    pincode: "",
    lat: null,
    lng: null,
    label: "All Locations",
    radius: 9999,
    permission: "prompt", // prompt | granted | denied | unavailable
    isManual: false,
    source: "default"
};

const STORAGE_KEY = 'im_customer_location';
const PERMISSION_ASKED_KEY = 'im_location_permission_asked';

/**
 * Format clean label avoiding duplicates like "Ahmedgarh, Ahmedgarh 148021"
 */
function formatLocationLabel(area, city, pincode) {
    const cleanArea = (area || '').trim();
    const cleanCity = (city || '').trim();
    const pin = pincode ? ` ${pincode}` : '';

    if (!cleanArea && !cleanCity) return 'Current Location';
    if (!cleanArea || cleanArea.toLowerCase() === cleanCity.toLowerCase() || cleanArea === 'Nearby Area') {
        return `${cleanCity}${pin}`;
    }
    return `${cleanArea}, ${cleanCity}${pin}`;
}

/**
 * Reverse-geocode using Nominatim API.
 */
async function reverseGeocode(lat, lng) {
    try {
        const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=16&addressdetails=1`;
        const res = await fetch(url, {
            headers: {
                'Accept-Language': 'en',
                'User-Agent': 'IMOnlineShop/1.0 (imonline.shop)',
            },
        });
        const json = await res.json();
        const a = json?.address ?? {};

        const area =
            a.suburb ||
            a.neighbourhood ||
            a.quarter ||
            a.village ||
            a.town ||
            a.city_district ||
            '';

        const city =
            a.city || a.town || a.county || a.state_district || 'Current Location';

        const pincode = a.postcode || '';
        return { city, area, pincode };
    } catch {
        return { city: 'Current Location', area: '', pincode: '' };
    }
}

/**
 * IP Geolocation Fallback
 * Used when browser Geolocation is unavailable, denied, or slow on desktop.
 */
async function detectIPLocation() {
    try {
        const res = await fetch('https://ipwho.is/');
        const data = await res.json();
        if (data && data.success && data.latitude && data.longitude) {
            const city = data.city || data.region || 'Current Location';
            const area = data.region || city;
            const pincode = data.postal || '';
            const lat = Number(data.latitude);
            const lng = Number(data.longitude);
            return {
                lat,
                lng,
                city,
                area,
                pincode,
                label: formatLocationLabel(area, city, pincode),
                radius: 'auto',
                isManualRadius: false,
                isManual: false,
                permission: 'granted',
                source: 'ip'
            };
        }
    } catch {
        // ignore
    }
    return null;
}

export const LocationProvider = ({ children }) => {
    const [location, setLocation] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                // Migrate old hardcoded Delhi default → All Locations
                if (
                    parsed.lat === 28.6139 &&
                    parsed.lng === 77.209 &&
                    parsed.permission === 'prompt' &&
                    parsed.area === 'Connaught Place'
                ) {
                    return DEFAULT_LOCATION;
                }
                // Clear stale unverified Ahmedgarh cache if not manually selected
                if (
                    !parsed.isManual &&
                    parsed.city?.toLowerCase() === 'ahmedgarh' &&
                    (parsed.label?.includes('Ahmedgarh, Ahmedgarh') || parsed.permission === 'prompt')
                ) {
                    return DEFAULT_LOCATION;
                }
                return parsed;
            }
            return DEFAULT_LOCATION;
        } catch {
            return DEFAULT_LOCATION;
        }
    });

    const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
    const [isDetecting, setIsDetecting] = useState(false);
    const [gpsStatus, setGpsStatus] = useState('idle'); // idle | requesting | granted | denied | unavailable

    // Persist location whenever it changes
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(location));
        } catch {
            // ignore
        }
    }, [location]);

    /**
     * Core GPS detect function.
     * Requests fresh browser Geolocation (maximumAge: 0).
     * If GPS fails or is denied, automatically falls back to live IP geolocation.
     */
    const detectGPSLocation = useCallback(async () => {
        setIsDetecting(true);
        setGpsStatus('requesting');

        try {
            localStorage.setItem(PERMISSION_ASKED_KEY, 'true');
        } catch { /* ignore */ }

        if (!navigator.geolocation) {
            const ipLoc = await detectIPLocation();
            if (ipLoc) {
                setLocation(ipLoc);
                setGpsStatus('granted');
            } else {
                setGpsStatus('unavailable');
            }
            setIsDetecting(false);
            return ipLoc;
        }

        return new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const lat = position.coords.latitude;
                    const lng = position.coords.longitude;

                    const { city, area, pincode } = await reverseGeocode(lat, lng);

                    const gpsLocation = {
                        lat,
                        lng,
                        city,
                        area,
                        pincode,
                        label: formatLocationLabel(area, city, pincode),
                        radius: 'auto',
                        isManualRadius: false,
                        isManual: false,
                        permission: 'granted',
                        source: 'gps'
                    };

                    setLocation(gpsLocation);
                    setIsDetecting(false);
                    setGpsStatus('granted');
                    setIsLocationModalOpen(false);
                    resolve(gpsLocation);
                },
                async () => {
                    // Browser GPS error or permission denied: fallback to IP location
                    const ipLoc = await detectIPLocation();
                    if (ipLoc) {
                        setLocation(ipLoc);
                        setGpsStatus('granted');
                        setIsLocationModalOpen(false);
                        resolve(ipLoc);
                    } else {
                        setGpsStatus('denied');
                        setLocation(prev => ({ ...prev, permission: 'denied' }));
                        resolve(null);
                    }
                    setIsDetecting(false);
                },
                { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
            );
        });
    }, []);

    /**
     * Live location check on mount:
     * If browser location was already granted, silently update to fresh coordinates.
     * If location is not locked manually by user, detect live location.
     */
    useEffect(() => {
        const syncLiveLocation = async () => {
            if (navigator.permissions && navigator.permissions.query) {
                try {
                    const result = await navigator.permissions.query({ name: 'geolocation' });
                    if (result.state === 'granted') {
                        detectGPSLocation();
                        return;
                    }
                } catch {
                    // permissions.query not supported
                }
            }

            // If not manually chosen, trigger live detection
            const saved = localStorage.getItem(STORAGE_KEY);
            let parsed = null;
            try { parsed = saved ? JSON.parse(saved) : null; } catch { /* ignore */ }

            if (!parsed || !parsed.isManual || parsed.city === 'All Locations') {
                const timer = setTimeout(() => {
                    detectGPSLocation();
                }, 800);
                return () => clearTimeout(timer);
            }
        };

        syncLiveLocation();
    }, [detectGPSLocation]);

    /** Called by "Use My Location" button manually */
    const detectCurrentLocation = useCallback(() => {
        return detectGPSLocation();
    }, [detectGPSLocation]);

    /** Set a manually-chosen city / area */
    const setManualLocation = useCallback((cityData) => {
        const newLocation = {
            ...location,
            ...cityData,
            label: cityData.label || `${cityData.city} ${cityData.pincode || ''}`.trim(),
            permission: 'granted',
            isManual: true,
            source: 'manual'
        };
        setLocation(newLocation);
        setIsLocationModalOpen(false);
        try { localStorage.setItem(PERMISSION_ASKED_KEY, 'true'); } catch { /* ignore */ }
    }, [location]);

    /** Update search radius */
    const setRadius = useCallback((newRadius, isManual = true) => {
        setLocation(prev => ({
            ...prev,
            radius: newRadius,
            isManualRadius: isManual
        }));
    }, []);

    return (
        <LocationContext.Provider
            value={{
                location,
                isLocationModalOpen,
                setIsLocationModalOpen,
                detectCurrentLocation,
                setManualLocation,
                setRadius,
                isDetecting,
                gpsStatus,
            }}
        >
            {children}
        </LocationContext.Provider>
    );
};

export const useLocation = () => {
    const context = useContext(LocationContext);
    if (!context) {
        return {
            location: DEFAULT_LOCATION,
            isLocationModalOpen: false,
            setIsLocationModalOpen: () => {},
            detectCurrentLocation: () => {},
            setManualLocation: () => {},
            setRadius: () => {},
            isDetecting: false,
            gpsStatus: 'idle',
        };
    }
    return context;
};
